/**
 * Keeps one editor in step with the same plan open in other tabs.
 *
 * The port of the server era's `usePlanChannel`, minus the network: another tab
 * announces "this plan is now at version N" over a `BroadcastChannel`, and the
 * document itself always travels through storage and the merge
 * (`pullAndIntegrate`), so this file contains no document logic. A message can
 * be missed — a tab frozen in the background hears nothing — so becoming
 * visible or focused again also compares versions.
 */

import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'

import { api } from '../lib/api'
import { useT } from '../lib/i18n/I18nProvider'
import { onTabMessage } from '../lib/local/tabs'
import { pullAndIntegrate } from '../lib/sync'
import { usePlanStore } from '../store/planStore'

export function useTabSync(planId: string | null, enabled: boolean): void {
  const t = useT()
  const navigate = useNavigate()
  const tRef = useRef(t)
  tRef.current = t

  useEffect(() => {
    if (!planId || !enabled) return undefined
    const store = () => usePlanStore.getState()

    const catchUpIfBehind = async (storedVersion: number) => {
      const current = store()
      if (!current.plan || storedVersion <= current.plan.version) return
      // Our own write is in flight: its ack is about to move us to that version.
      // Integrating the stored copy of what we just sent would let it overrule
      // edits made since; if another tab really did get ahead, the save's 409
      // brings its work in through the same merge.
      if (current.saveStatus === 'saving') return
      await pullAndIntegrate(planId)
    }

    const off = onTabMessage((message) => {
      if (message.planId !== planId) return
      if (message.type === 'saved') {
        void catchUpIfBehind(message.version)
      } else {
        store().showToast(tRef.current.editor.deletedElsewhere, 'warn')
        navigate('/plans')
      }
    })

    const onWake = () => {
      if (document.visibilityState !== 'visible') return
      void api
        .planVersion(planId)
        .then(({ version }) => catchUpIfBehind(version))
        .catch(() => undefined)
    }
    document.addEventListener('visibilitychange', onWake)
    window.addEventListener('focus', onWake)

    return () => {
      off()
      document.removeEventListener('visibilitychange', onWake)
      window.removeEventListener('focus', onWake)
    }
  }, [planId, enabled, navigate])
}
